import { resolveEngineFrame, syntheticRollSource } from '../actions/ability-use.js';
import { activeStatuses, ec, ecId, ecLean, ecChanges, HandBack, handBack, insertRoll, record, upper, type JanusCtx, type Row } from '../ability/context.js';
import { frameCopyOnRead } from '../ability/frame-snapshot.js';
import { carrierHasTrigger } from '../ability/statuses/status-trigger-gate.js';
import { endEffectsOnStatusGain } from '../ability/statuses/apply-status-effect.js';
import { runTriggerFamily } from '../ability/trigger-executor.js';
import { cpiFoldDeps } from '../cpi/heal.js';
import { forcedMovementReaders, layerKeysDeep } from '../ability/movement/check-push-and-pull-after-hit.js';
import { resolveLandingSupport } from '../statuses/removal-support.js';
import { statusItemId, statusRows, statusCasterId, removeStatusFromRow } from '../statuses/expiry.js';
import { statusScalar } from '../actions/activity.js';
import { readStatusRowStack } from '../compile/status-stack-reader.js';
import { SPEED, UNDERWATER_ENVIRONMENT, predictSink } from '../movement/fall-check.js';
import { encounterField, layerListEntryUuid } from '../../shared/array-types.js';
import { speedFeetFromCluster } from '../../shared/hunker-visibility.js';
import { prepareTurnTick, turnTickStatusFrames } from './tick-frame.js';
import { statusInputSnapshot } from './input-snapshots.js';
import type { TurnTickReaders, TurnTickResult } from './ticks.js';
import type { AbilityEntry } from './end-turn.js';
import type { RestShape } from '../inventory/floor-move.js';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers.js';

/** Primary status rows are saved by the existing time command; this frame owns only their consequences. */
export interface HungerStatusEvent { kind: 'gain' | 'loss' | 'stack'; status: Row }
export interface HungerConsequencePlan { actorId: string; events: HungerStatusEvent[] }
const statusId = (status: Row): string => upper(statusScalar(status, 'status_id'));

export function resolveHungerConsequences(input: { encounter: Row; rows: Row[]; plans: HungerConsequencePlan[];
  removedIds: ReadonlySet<string>; meters: Array<{ actor_id: string; hunger_to: number; thirst_to: number }>;
  primarySkills: (actorId: string) => Record<string, Row> | undefined;
  read: TurnTickReaders; entries: (row: Row) => AbilityEntry[]; rollPrivate: number; preflightOnly: boolean }):
  { supported: true; tick: TurnTickResult; before: ReturnType<typeof prepareTurnTick>['before'] } | { supported: false; actorId: string; reason: string } {
  const source = new Map(input.rows.map(row => [ecId(row), row]));
  const base = frameCopyOnRead(input.encounter, source, { catalogs: 'share' });
  for (const [id, row] of base.members) {
    for (const statusId of input.removedIds) removeStatusFromRow(row, statusId);
    const meter = input.meters.find(m => m.actor_id === id);
    if (meter) { ec(row).hunger_meter_current = meter.hunger_to; ec(row).thirst_meter_current = meter.thirst_to; }
  }
  const primary = new Map(input.plans.flatMap(p => p.events.map(e => [statusId(e.status), e.status] as const)));
  const primaryRemoved = new Set(input.plans.flatMap(p => p.events.filter(e => e.kind === 'loss').map(e => statusId(e.status))));
  const baseRows = Array.from(base.members.values());
  const native = input.read;
  const perform = (ctx: JanusCtx): TurnTickResult => {
    for (const plan of input.plans) {
      try {
        const member = ctx.members.get(plan.actorId)!;
        const refreshSpeed = (definitions: Row[]): void => {
          const changed = definitions.some(def => {
            const keys = layerKeysDeep(ctx, def);
            if (keys === null) handBack('support-definition-tree-not-held');
            return keys.some(k => /^speed_|^movement_|^teleport$|shapeshift/.test(k));
          });
          if (!changed) return;
          const host = { ...member, statuses: { ...record(member.statuses), target: activeStatuses(ctx, member) } };
          const result = cpiFoldDeps(ctx.read.definition).compileSpeeds?.(host, true);
          if (!result?.ok) handBack('speed-compile:' + (result?.reason ?? 'unavailable'));
          const computed = (member.computed ??= {}) as Row, creature = (computed.creature ??= {}) as Row;
          creature.speeds = result.speeds;
        };
        const removed = statusRows(source.get(plan.actorId)!, 'target').filter(s => input.removedIds.has(statusId(s)));
        refreshSpeed(removed.map(s => ctx.read.definition(statusItemId(s)) ?? record(s.status_arr) ?? handBack('removal-definition-not-held:' + statusItemId(s))));
        for (const event of plan.events) {
          const status = event.status, itemId = statusItemId(status);
          // An earlier gain hook may already have removed the transient flag and fired its loss arm.
          if (event.kind === 'loss' && !activeStatuses(ctx, member).some(s => statusId(s) === statusId(status))) continue;
          const beforeEffects = activeStatuses(ctx, member);
          if (event.kind === 'gain' && beforeEffects.some(s => statusItemId(s) === itemId))
            handBack('primary-status-changed-by-listener:' + itemId);
          const definition = ctx.read.definition(itemId);
          if (!definition) handBack('definition-not-held:' + itemId);
          if (event.kind === 'stack') {
            const held = statusRows(member, 'target').find(s => statusId(s) === statusId(status));
            if (!held || readStatusRowStack(held) !== readStatusRowStack(status) - 1)
              handBack('primary-stack-changed-by-listener:' + itemId);
            held.status_stack = readStatusRowStack(status);
          } else if (event.kind === 'loss') removeStoredStatus(ctx, member, status, 'single', { deferSupport: true });
          else {
            const entry = { ...status, status_arr: definition };
            const statuses = (member.statuses ??= {}) as Row;
            for (const mode of ['target', 'caster']) statuses[mode] = [entry, ...statusRows(member, mode as 'target' | 'caster')];
          }
          if (event.kind === 'gain') {
            const trigger = 'triggers_when_caster_gains_status_effect';
            if (carrierHasTrigger(ctx, member, trigger, 'hunger-trigger', itemId))
              runTriggerFamily(ctx, { trigger, statusItemId: itemId }, member, member, [member], false);
            endEffectsOnStatusGain(ctx, member, itemId,
              statusRows(member, 'target').find(s => statusId(s) === statusId(status)));
          }
          const afterEffects = activeStatuses(ctx, member);
          const effectChanges = [...beforeEffects.filter(s => !afterEffects.includes(s)), ...afterEffects.filter(s => !beforeEffects.includes(s))];
          refreshSpeed([definition, ...effectChanges.map(s => ctx.read.definition(statusItemId(s)) ?? record(s.status_arr) ?? handBack('support-definition-not-held:' + statusItemId(s)))]);
          const preferred = upper(ecLean(member, 'preferred_speed_type'));
          if (preferred !== SPEED.flying && preferred !== SPEED.swimming) continue;
          const kind = preferred === SPEED.flying ? 'flying' : 'swimming';
          const speeds = record(record(record(member.computed)?.creature)?.speeds);
          // A held speed cluster may elide its zero slot; authored layers cannot revive that speed.
          const speed = speeds ? speedFeetFromCluster(speeds, kind) ?? 0 : Number(ctx.read.value(member, 'speed_' + kind) ?? 0);
          if (speed > 0) continue;
          if (preferred === SPEED.flying) resolveLandingSupport(ctx, plan.actorId, speeds ?? undefined);
          else if (upper(layerListEntryUuid(encounterField(ctx.encounter, 'encounter_environment')
            ?? ctx.read.value(ctx.encounter, 'location_environment'))) === UNDERWATER_ENVIRONMENT) {
            const raw = encounterField(ctx.encounter, 'encounter_shape');
            const shape = record(typeof raw === 'string' && raw ? JSON.parse(raw) : raw) as RestShape | null;
            const result = predictSink({ row: member, encounter: ctx.encounter, shape, legacyCentre: true }, forcedMovementReaders(ctx));
            if (result.kind === 'handback') handBack('sink:' + result.reason);
            const z = result.kind === 'sink' ? result.landingZ : result.snapZ;
            if (z !== undefined) { ((member.position ??= {}) as Row).z_pos = z; ec(member).z_pos = z; }
            if (result.kind === 'sink') insertRoll(ctx, { roll_type_1: 'creature_sank', by: member, roll_result: Math.round(result.sinkFt) });
          }
        }
      } catch (error) {
        if (error instanceof HandBack) throw new HandBack('hunger-consequence:' + plan.actorId + ':' + error.reason);
        throw error;
      }
    }
    // The time save accepts native tick effects, not arbitrary ability-only mutations.
    if (ctx.inventoryChanged.length || ctx.playerCharactersChanged?.length || ctx.chargesRemoved?.length
      || ctx.turnTransition || ctx.turnDeathNone || ctx.weaponInventoryChanges?.length || ctx.shapeshiftInventory?.length
      || ctx.concentrationBreaks?.length || ctx.deathTail?.length || ctx.dyingTail?.length || ctx.summons?.length)
      handBack('hunger-consequence:' + input.plans[0]!.actorId + ':tick-contract');
    const tick: TurnTickResult = { rows: ctx.rows, ec: ecChanges(ctx), statusesAdded: ctx.statusesAdded,
      statusesRemoved: ctx.statusesRemoved.filter(r => !primaryRemoved.has(upper(r.status_id))).map(r => {
        const before = primary.get(upper(r.status_id)) ?? statusRows(source.get(upper(r.target)) ?? {}, 'target').find(s => statusId(s) === upper(r.status_id));
        if (!before) handBack('hunger-consequence:' + input.plans[0]!.actorId + ':removal-before-not-held');
        return { status_id: r.status_id, status_item_id: r.status_item_id, target: r.target,
          caster: statusCasterId(before), before: statusInputSnapshot(before) };
      }), statusesChanged: (ctx.statusStacks ?? []).map(s => ({ status_id: s.status_id, status_stack: s.status_stack })), charges: ctx.chargesChanged,
      ...(ctx.spawnRows ? { spawnRows: ctx.spawnRows } : {}),
      ...(ctx.statusTriggerListeners?.length ? { statusTriggerListeners: ctx.statusTriggerListeners } : {}),
      ...(ctx.teleportOffers?.length ? { teleportOffers: ctx.teleportOffers } : {}),
      ...(ctx.fortunateOffers?.size ? { fortunateOffers: [...ctx.fortunateOffers] } : {}) };
    for (const status of tick.statusesAdded) (status as unknown as Row).status_id ??= ctx.read.uuid();
    // Consequences run after the primary hunger frame on every client. Derive
    // their skill deltas from that same primary projection, including Exhausted.
    tick.statusFrames = turnTickStatusFrames(baseRows, tick, native.ability.definition, input.primarySkills);
    const prepared = prepareTurnTick(baseRows, tick, input.entries);
    tick.charges = prepared.charges;
    return tick;
  };
  const result = resolveEngineFrame({ rollPrivate: input.rollPrivate, dryRun: input.preflightOnly }, base.encounter, base.members,
    native.ability, input.preflightOnly ? syntheticRollSource('min') : native.roll, () => {}, perform);
  if (result.kind === 'handback') {
    const match = /(?:hunger-consequence|turn-tick):([0-9A-F-]{36}):(.*)/.exec(result.reason);
    const actorId = input.plans.find(plan => plan.actorId === match?.[1])?.actorId ?? input.plans[0]!.actorId;
    return { supported: false, actorId, reason: match?.[2] ?? result.reason };
  }
  return { supported: true, tick: result.value, before: prepareTurnTick(baseRows, result.value, input.entries).before };
}
